import { createLogger } from "@/lib/logger";
import type { AvatarView } from "@spatius/avatarkit";

const logger = createLogger("spatius-webgl-resize");

type Target = {
  framebuffer: WebGLFramebuffer | null;
  renderTexture: WebGLTexture | null;
  depthBuffer: WebGLRenderbuffer | null;
};

type Renderer = Target & {
  gl: WebGL2RenderingContext;
  framebufferWidth: number;
  framebufferHeight: number;
  splatBufferSize: number;
  isInitialized: boolean;
  initialize: () => Promise<void>;
  dispose: () => void;
  createFramebuffer: (width: number, height: number) => void;
};

type RuntimeView = {
  renderSystem?: { getBackend: () => string; renderer: Renderer };
  syncCanvasSize: (canvas: HTMLCanvasElement, container: HTMLElement) => void;
  computeCappedBacking: (
    width: number,
    height: number,
    dpr: number
  ) => { width: number; height: number };
  getCanvas: () => HTMLCanvasElement;
  pauseRendering: () => void;
  resumeRendering: () => void;
  dispose: () => void;
};

function release(gl: WebGL2RenderingContext, target: Target) {
  if (target.framebuffer) gl.deleteFramebuffer(target.framebuffer);
  if (target.renderTexture) gl.deleteTexture(target.renderTexture);
  if (target.depthBuffer) gl.deleteRenderbuffer(target.depthBuffer);
}

function allocate(
  gl: WebGL2RenderingContext,
  width: number,
  height: number,
  format: number,
  depth: boolean
) {
  const target: Target = {
    framebuffer: gl.createFramebuffer(),
    renderTexture: gl.createTexture(),
    depthBuffer: depth ? gl.createRenderbuffer() : null,
  };
  try {
    if (!target.framebuffer || !target.renderTexture || (depth && !target.depthBuffer)) {
      throw new Error("WebGL framebuffer resource allocation failed");
    }
    gl.bindTexture(gl.TEXTURE_2D, target.renderTexture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      format,
      width,
      height,
      0,
      gl.RGBA,
      format === gl.RGBA16F ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      target.renderTexture,
      0
    );
    if (target.depthBuffer) {
      gl.bindRenderbuffer(gl.RENDERBUFFER, target.depthBuffer);
      gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, width, height);
      gl.framebufferRenderbuffer(
        gl.FRAMEBUFFER,
        gl.DEPTH_ATTACHMENT,
        gl.RENDERBUFFER,
        target.depthBuffer
      );
    }
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error("WebGL framebuffer replacement is incomplete");
    }
    return target;
  } catch (error) {
    release(gl, target);
    throw error;
  }
}

/**
 * Adapter for the pinned AvatarKit 1.3.10 renderer. The SDK does not expose a
 * framebuffer allocator. Keep the private integration here and revalidate it
 * when upgrading the SDK; WebGPU and the RTC/audio controller are untouched.
 */
export function protectSpatiusWebGlResize(view: AvatarView, shouldRender: () => boolean) {
  const runtime = view as unknown as RuntimeView;
  const system = runtime.renderSystem;
  if (system?.getBackend?.() !== "webgl") return () => {};
  const renderer = system.renderer;
  const canvas = runtime.getCanvas();
  const originalAllocate = renderer.createFramebuffer;
  const originalResize = runtime.syncCanvasSize;
  const originalBacking = runtime.computeCappedBacking;
  const originalDispose = runtime.dispose;
  const gl = renderer.gl;
  const maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  const maxRenderbuffer = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number;
  const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array;
  const maxWidth = Math.min(maxTexture, maxRenderbuffer, maxViewport[0]);
  const maxHeight = Math.min(maxTexture, maxRenderbuffer, maxViewport[1]);
  runtime.computeCappedBacking = (width, height, dpr) => {
    const size = originalBacking.call(view, width, height, dpr);
    const scale = Math.min(1, maxWidth / size.width, maxHeight / size.height);
    return {
      width: Math.max(1, Math.floor(size.width * scale)),
      height: Math.max(1, Math.floor(size.height * scale)),
    };
  };
  const formats = [
    { format: renderer.gl.RGBA16F, depth: true },
    // AvatarKit always disables depth testing for its blended splats. Removing
    // the unused depth attachment retains float color precision.
    { format: renderer.gl.RGBA16F, depth: false },
    { format: renderer.gl.RGBA8, depth: false },
  ];
  let preferred = 0;
  let disposed = false;
  let restoring = false;
  let resizeFrame: number | undefined;
  let pendingResize: [HTMLCanvasElement, HTMLElement] | undefined;

  renderer.createFramebuffer = (width, height) => {
    const gl = renderer.gl;
    if (gl.isContextLost()) throw new Error("Avatar WebGL context is lost");
    const old: Target = {
      framebuffer: renderer.framebuffer,
      renderTexture: renderer.renderTexture,
      depthBuffer: renderer.depthBuffer,
    };
    const drawBinding = gl.getParameter(gl.DRAW_FRAMEBUFFER_BINDING);
    const readBinding = gl.getParameter(gl.READ_FRAMEBUFFER_BINDING);
    const textureBinding = gl.getParameter(gl.TEXTURE_BINDING_2D);
    const depthBinding = gl.getParameter(gl.RENDERBUFFER_BINDING);
    let replacement: Target | undefined;
    try {
      for (let index = preferred; index < formats.length && !gl.isContextLost(); index += 1) {
        try {
          const candidate = formats[index];
          replacement = allocate(gl, width, height, candidate.format, candidate.depth);
          if (index !== preferred) {
            logger.warn("Avatar framebuffer using compatible render target", {
              width,
              height,
              format: index,
            });
          }
          preferred = index;
          break;
        } catch {
          // Candidate resources have been released; the working target is intact.
        }
      }
      if (!replacement) {
        // Do not keep allocating once all supported combinations fail. Context
        // restoration can resume this view; audio continues while drawing pauses.
        runtime.pauseRendering();
        throw new Error("Avatar WebGL framebuffer allocation failed for all compatible formats");
      }
      Object.assign(renderer, replacement, { framebufferWidth: width, framebufferHeight: height });
    } finally {
      // Never leave a half-built target bound after a failure. Unbind an old
      // attachment before releasing it after a successful transactional swap.
      gl.bindFramebuffer(
        gl.DRAW_FRAMEBUFFER,
        drawBinding === old.framebuffer && replacement ? null : drawBinding
      );
      gl.bindFramebuffer(
        gl.READ_FRAMEBUFFER,
        readBinding === old.framebuffer && replacement ? null : readBinding
      );
      gl.bindTexture(
        gl.TEXTURE_2D,
        textureBinding === old.renderTexture && replacement ? null : textureBinding
      );
      gl.bindRenderbuffer(
        gl.RENDERBUFFER,
        depthBinding === old.depthBuffer && replacement ? null : depthBinding
      );
      if (replacement) release(gl, old);
    }
  };

  runtime.syncCanvasSize = (element, container) => {
    pendingResize = [element, container];
    if (resizeFrame !== undefined) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = undefined;
      if (disposed || restoring || !pendingResize) return;
      const [target, parent] = pendingResize;
      pendingResize = undefined;
      const width = Math.floor(parent.offsetWidth);
      const height = Math.floor(parent.offsetHeight);
      if (!width || !height) return;
      const backing = runtime.computeCappedBacking(width, height, window.devicePixelRatio || 1);
      if (
        target.width === backing.width &&
        target.height === backing.height &&
        target.style.width === `${width}px` &&
        target.style.height === `${height}px`
      )
        return;
      originalResize.call(view, target, parent);
    });
  };

  const onLost = (event: Event) => {
    event.preventDefault();
    restoring = true;
    runtime.pauseRendering();
  };
  const onRestored = async () => {
    if (disposed) return;
    try {
      // Restored WebGL contexts invalidate every old GPU object, not just the
      // framebuffer. Reinitialize the renderer while retaining avatar/WASM/RTC.
      Object.assign(renderer, {
        framebuffer: null,
        renderTexture: null,
        depthBuffer: null,
        framebufferWidth: 0,
        framebufferHeight: 0,
        splatBufferSize: 0,
        isInitialized: false,
      });
      preferred = 0;
      await renderer.initialize();
      if (disposed) {
        renderer.dispose();
        return;
      }
      restoring = false;
      if (pendingResize) runtime.syncCanvasSize(...pendingResize);
      if (shouldRender()) runtime.resumeRendering();
    } catch (error) {
      logger.error("Avatar WebGL context restoration failed", { error: String(error) });
    }
  };
  canvas.addEventListener("webglcontextlost", onLost);
  canvas.addEventListener("webglcontextrestored", onRestored);

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
    canvas.removeEventListener("webglcontextlost", onLost);
    canvas.removeEventListener("webglcontextrestored", onRestored);
    renderer.createFramebuffer = originalAllocate;
    runtime.syncCanvasSize = originalResize;
    runtime.computeCappedBacking = originalBacking;
    runtime.dispose = originalDispose;
  };
  // Manual disconnect also calls view.dispose(), even if the React effect has
  // not unmounted yet. A retired view must never reinitialize a lost context.
  runtime.dispose = () => {
    cleanup();
    originalDispose.call(view);
  };
  return cleanup;
}
